import DOMPurify from 'dompurify'

// Reverse-tabnabbing guard: any sanitized link that opens a new browsing context
// (target set) must not retain window.opener access. DOMPurify hooks are global to
// this instance, so this applies to every sanitizeHtml / sanitizeArticleHtml call.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('target')) {
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

/**
 * Escape HTML special characters in a text segment so user-authored content
 * cannot inject markup when interpolated into HTML text or an attribute value.
 * @param {unknown} str
 * @returns {string}
 */
export function escapeHtmlText(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// The URL-scheme policy lives in its own DOM-free module so pure consumers
// (the Markdown export serializer, workers) can use it without importing this
// file — importing THIS module installs the DOMPurify hook above and pulls
// DOMPurify into the caller's chunk. Re-exported here so every existing
// `from '.../security.js'` import keeps working.
export { isSafeHref, safeHref, safeImageSrc } from './safeHref.js'

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} dirty - Unsanitized HTML content
 * @param {object} config - DOMPurify configuration options
 * @returns {string} - Sanitized HTML
 */
export function sanitizeHtml(dirty, config = {}) {
  if (!dirty) return ''
  
  // Default safe configuration
  const defaultConfig = {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr', 'blockquote', 'pre', 'code',
      'ul', 'ol', 'li',
      'a', 'img',
      'strong', 'em', 'u', 's', 'mark',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span',
      'figure', 'figcaption'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id',
      'width', 'height', 'style', 'target', 'rel'
    ],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    // L7 — `data:` removed from the allowed scheme set. Stored images are rehosted
    // to img.linkflare.io, so data: URIs are unnecessary here and broaden the XSS
    // surface (e.g. data:text/html in an href). Relative URLs are still allowed.
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
    ...config
  }
  
  return DOMPurify.sanitize(dirty, defaultConfig)
}

/**
 * Sanitize HTML for article content with more permissive rules
 * @param {string} dirty - Unsanitized HTML content
 * @returns {string} - Sanitized HTML
 */
export function sanitizeArticleHtml(dirty) {
  return sanitizeHtml(dirty, {
    ALLOWED_TAGS: [
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'p', 'br', 'hr', 'blockquote', 'pre', 'code',
      'ul', 'ol', 'li',
      'a', 'img',
      'strong', 'em', 'u', 's', 'mark', 'small', 'sup', 'sub',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'article', 'section', 'header', 'footer',
      'figure', 'figcaption', 'picture', 'source',
      'video', 'audio', 'iframe'
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id',
      'width', 'height', 'style', 'target', 'rel',
      'type', 'controls', 'autoplay', 'loop', 'muted',
      'poster', 'preload', 'srcset', 'sizes',
      'allow', 'allowfullscreen', 'frameborder',
      // Inert hardening/perf hints that were being stripped: without them a PDF
      // embed (utils/pdfEmbed.js) leaked a referrer and loaded eagerly here.
      'loading', 'referrerpolicy'
    ],
    ADD_ATTR: ['target'],
    // Automatically add rel="noopener noreferrer" to links
    SAFE_FOR_TEMPLATES: true
  })
}

/**
 * Validate and sanitize URLs
 * @param {string} url - URL to validate
 * @param {array} allowedProtocols - Allowed protocols (default: ['http:', 'https:'])
 * @returns {string|null} - Validated URL or null if invalid
 */
export function validateUrl(url, allowedProtocols = ['http:', 'https:']) {
  if (!url || typeof url !== 'string') return null
  
  // Prepend https:// if no protocol is present
  let normalizedUrl = url.trim()
  if (!/^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(normalizedUrl)) {
    normalizedUrl = 'https://' + normalizedUrl
  }
  
  try {
    const urlObj = new URL(normalizedUrl)
    
    // Check if protocol is allowed
    if (!allowedProtocols.includes(urlObj.protocol)) {
      console.warn('Invalid URL protocol:', urlObj.protocol)
      return null
    }
    
    // Prevent dangerous URL schemes that could execute scripts or access local resources
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:', 'about:']
    if (dangerousProtocols.some(proto => urlObj.protocol === proto || url.toLowerCase().startsWith(proto))) {
      console.warn('Blocked potentially malicious URL protocol:', urlObj.protocol)
      return null
    }
    
    return urlObj.href
  } catch (e) {
    console.warn('Invalid URL:', url, e)
    return null
  }
}

/**
 * Safely open URL in new tab with security measures.
 * IMPORTANT: Do NOT pass a features string (3rd arg) to window.open() —
 * it triggers popup blocking in Android PWA standalone mode.
 * @param {string} url - URL to open
 * @returns {boolean} - True if URL was opened, false otherwise
 */
export function safeOpenUrl(url) {
  const validatedUrl = validateUrl(url)
  
  if (!validatedUrl) {
    console.error('Cannot open invalid URL:', url)
    return false
  }
  
  // Primary: window.open without a features string (a features string trips
  // Android PWA popup blocking). window.open does NOT get an implicit noopener
  // the way <a target=_blank> does, so sever the opener link explicitly to
  // prevent reverse tabnabbing (the opened page reaching back via window.opener).
  const newWindow = window.open(validatedUrl, '_blank')
  if (newWindow) {
    try { newWindow.opener = null } catch (e) { /* cross-origin: opener already unreachable */ }
  }
  
  // Fallback: if window.open was blocked (returns null), use anchor click
  if (!newWindow) {
    const a = document.createElement('a')
    a.href = validatedUrl
    a.target = '_blank'
    a.rel = 'noopener noreferrer'
    // Use offscreen positioning instead of display:none (some Android browsers
    // ignore clicks on display:none elements)
    a.style.position = 'fixed'
    a.style.left = '-9999px'
    a.style.top = '-9999px'
    document.body.appendChild(a)
    a.click()
    // Delay removal so the navigation has time to start
    setTimeout(() => {
      try { document.body.removeChild(a) } catch (e) { /* already removed */ }
    }, 200)
  }
  
  return true
}

/**
 * Sanitize text content (strip all HTML)
 * @param {string} text - Text that may contain HTML
 * @returns {string} - Plain text
 */
export function sanitizeText(text) {
  if (!text) return ''
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] })
}

/**
 * Decode HTML entities in a plain-text string (e.g. &#8217; → ')
 * Uses DOMParser so no untrusted HTML is injected into the live DOM.
 * @param {string} text - Text that may contain HTML entities
 * @returns {string} - Decoded plain text
 */
export function decodeHtmlEntities(text) {
  if (!text) return ''
  const doc = new DOMParser().parseFromString(text, 'text/html')
  return doc.documentElement.textContent
}

/**
 * Check if a password meets minimum security requirements
 * @param {string} password - Password to validate
 * @returns {object} - Validation result with isValid and message
 */
export function validatePassword(password) {
  if (!password || typeof password !== 'string') {
    return { isValid: false, message: 'Password is required' }
  }
  
  if (password.length < 8) {
    return { isValid: false, message: 'Password must be at least 8 characters long' }
  }
  
  // Check for at least one uppercase, one lowercase, and one number
  const hasUpperCase = /[A-Z]/.test(password)
  const hasLowerCase = /[a-z]/.test(password)
  const hasNumber = /[0-9]/.test(password)
  
  if (!hasUpperCase || !hasLowerCase || !hasNumber) {
    return { 
      isValid: false, 
      message: 'Password must contain at least one uppercase letter, one lowercase letter, and one number' 
    }
  }
  
  return { isValid: true, message: 'Password is valid' }
}
